import { Fragment } from 'react'
import cn from 'classnames'
import { NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { ReactComponent as IconMenus } from 'assets/icons/applications/IconMenus.svg'
import { ReactComponent as IconLoyalty } from 'assets/icons/applications/IconLoyalty.svg'
import { ReactComponent as IconDevices } from 'assets/icons/applications/IconDevices.svg'
import { ReactComponent as IconRefund } from 'assets/icons/applications/IconRefund.svg'
import { ReactComponent as IconSuites } from 'assets/icons/applications/IconSuites.svg'
import { ReactComponent as IconReports } from 'assets/icons/applications/IconReports.svg'
import { ReactComponent as IconStatus } from 'assets/icons/applications/IconStatus.svg'
import { ReactComponent as IconOrders } from 'assets/icons/applications/IconOrders.svg'
import { ReactComponent as IconAccess } from 'assets/icons/applications/IconAccess.svg'
import { ReactComponent as IconSettings } from 'assets/icons/applications/IconSettings.svg'

import { TSpaItem } from 'types/spa'

import useMobileDetect from 'hooks/useMobileDetect'
import { createSpaLink, getCurrentInstance } from 'utils/general/links'
import spas from 'utils/general/spas'
import { logOutAndRedirect } from 'utils/general/auth'
import { useGetOrganizationsQuery } from 'store/porticoApi/organizations/organizations'
import { getCurrentUserEmail } from 'store/appStateSlice/selectors'

type TSidebar = {
  isOpen: boolean
  toggleSidebarActive: () => void
}

type TAppIcons = {
  [key: string]: React.ReactElement
}

const Sidebar = ({ isOpen, toggleSidebarActive }: TSidebar) => {
  const { isMobile } = useMobileDetect()

  const { data: orgnization } = useGetOrganizationsQuery()

  const userEmail = useSelector(getCurrentUserEmail)

  const appsIcons: TAppIcons = {
    menus: <IconMenus />,
    orders: <IconOrders />,
    status: <IconStatus />,
    reports: <IconReports />,
    refund: <IconRefund />,
    loyalty: <IconLoyalty />,
    devices: <IconDevices />,
    suites: <IconSuites />,
    access: <IconAccess />,
    settings: <IconSettings />,
  }

  const handleLogout = () => {
    logOutAndRedirect()
  }

  const checkVenueNexter = (spaItem: object) => {
    const item = spaItem as TSpaItem
    return !item?.venuenexter_only || userEmail.endsWith('shift4.com')
  }

  const checkDevEnvironment = (spaItem: object) => {
    const item = spaItem as TSpaItem
    return (
      !item?.dev_only ||
      process.env.REACT_APP_APP_ENV === 'dev' ||
      process.env.REACT_APP_APP_ENV === 'qa'
    )
  }

  const instanceName = getCurrentInstance(window.location.href)

  return (
    <aside className={cn('sidebar', { '-open': isOpen })}>
      <div className="sidebar__wrapper">
        <header className="sidebar__header">{orgnization?.displayName ?? 'unknown'}</header>
        <nav className="sidebar__navigation">
          <section className="sidebar__apps">
            {spas.map((spa, index) => (
              <Fragment key={index}>
                <a
                  className="sidebar__item"
                  href={createSpaLink(spa.domain, spa.subdomain, instanceName)}
                  id={spa.name}
                >
                  {appsIcons[spa.name]}
                  <span className="sidebar__menu-title">{spa.display_name}</span>
                </a>
                {spa?.items && (
                  <>
                    {spa?.items?.map(
                      (item) =>
                        checkVenueNexter(item) &&
                        checkDevEnvironment(item) && (
                          <NavLink
                            className={({ isActive }) =>
                              'sidebar__sub-item' + (isActive ? ' -active' : '')
                            }
                            onClick={isMobile ? toggleSidebarActive : undefined}
                            to={item.link}
                            key={item.name}
                          >
                            <span className="sidebar__menu-title">{item.name}</span>
                          </NavLink>
                        )
                    )}
                    <div className="sidebar__sub-item-separator"></div>
                  </>
                )}
              </Fragment>
            ))}
          </section>
        </nav>
      </div>
      <div className="sidebar__info">
        <span className="sidebar__user-email">{userEmail}</span>
        <button className="sidebar__btn-logout" onClick={handleLogout} type="button">
          Log Out
        </button>
        <span className="sidebar__version-box">{process.env.REACT_APP_VERSION}</span>
      </div>
      <div
        className={cn('sidebar__overlay', { '-open': isOpen })}
        onClick={toggleSidebarActive}
      ></div>
    </aside>
  )
}
export default Sidebar
